import { dishes } from '../data/dishes';

/**
 * Turns dish names already written in a blog post into links to that dish's
 * page.
 *
 * This adds markup, never words: no text is inserted, removed or reworded. The
 * post files in public/blog-posts/ are untouched — the linking happens on the
 * HTML after it is fetched and before it is rendered.
 *
 * Why it matters: /blog/mango-sticky-rice is a whole article about the dish,
 * and it was passing none of that relevance to /dishes/mango-sticky-rice.
 *
 * Rules, to keep this from reading as keyword spam:
 *  - only the FIRST mention of each dish in a post is linked
 *  - never inside an existing <a>, a heading, or an HTML attribute
 *  - whole words only, so "Pad Thai" does not match inside "Pad Thai-style"
 *  - currentSlug guards against a page linking to itself. Blog slugs are a
 *    DIFFERENT url space from dish slugs, so BlogPost does not pass it:
 *    /blog/mango-sticky-rice linking to /dishes/mango-sticky-rice is the single
 *    most valuable link on the blog, not a self-link.
 */

/** Name variants worth matching, longest first so the fullest name wins. */
const buildPatterns = () =>
  dishes
    .flatMap((dish) => {
      const names = new Set<string>([dish.name, dish.menuName]);
      // "Drunken Noodles (Kee Mao)" should also match a bare "pad kee mao".
      if (dish.slug === 'drunken-noodles') names.add('pad kee mao');
      if (dish.slug === 'pad-see-ew') names.add('pad see ew');
      return [...names].map((name) => ({ slug: dish.slug, name }));
    })
    .sort((a, b) => b.name.length - a.name.length);

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Splits HTML into segments that are safe to rewrite (text nodes outside links
 * and headings) and segments that must be left alone.
 */
const SKIP_BLOCK = /<a\b[^>]*>[\s\S]*?<\/a>|<h[1-6]\b[^>]*>[\s\S]*?<\/h[1-6]>|<[^>]+>/gi;

export const linkDishMentions = (html: string, currentSlug?: string): string => {
  if (!html) return html;

  const patterns = buildPatterns().filter((p) => p.slug !== currentSlug);
  const linked = new Set<string>();

  // Walk the HTML, rewriting only the text that sits between tags and outside
  // anchors and headings.
  let result = '';
  let cursor = 0;

  for (const match of html.matchAll(SKIP_BLOCK)) {
    const start = match.index ?? 0;
    result += linkInText(html.slice(cursor, start), patterns, linked);
    result += match[0];
    cursor = start + match[0].length;
  }
  result += linkInText(html.slice(cursor), patterns, linked);

  return result;
};

const linkInText = (
  text: string,
  patterns: { slug: string; name: string }[],
  linked: Set<string>,
): string => {
  if (!text.trim()) return text;

  let out = text;
  for (const { slug, name } of patterns) {
    if (linked.has(slug)) continue;

    const re = new RegExp(`\\b${escapeRegExp(name)}\\b`, 'i');
    if (!re.test(out)) continue;

    out = out.replace(
      re,
      (matched) =>
        `<a href="/dishes/${slug}" class="dish-mention-link">${matched}</a>`,
    );
    linked.add(slug);
  }
  return out;
};
